// CPC LOCAL: full-width multi-panel leaderboards (validators, holders, CLOB).

import { formatAmount } from '@mysten/core';
import { useSuiClientQuery } from '@mysten/dapp-kit';
import { ArrowRight12 } from '@mysten/icons';
import { Text } from '@mysten/ui';
import { useQuery } from '@tanstack/react-query';
import { useMemo, type ReactNode } from 'react';
import {
	fetchClobOrderLeaderboard,
	fetchClobTraderLeaderboard,
	fetchCpcHolderLeaderboard,
	fetchCpcStakerLeaderboard,
	fetchDataHolderLeaderboard,
	fetchPushHubHealth,
	type AddressStatRank,
	type ClobOrderRank,
	type ClobTraderRank,
} from '~/hooks/pushHubClient';
import { ImageIcon } from '~/ui/ImageIcon';
import { AddressLink, ValidatorLink } from '~/ui/InternalLink';
import { Link } from '~/ui/Link';
import { PlaceholderTable } from '~/ui/PlaceholderTable';
import { TableCard } from '~/ui/TableCard';
import { CPC_SYMBOL } from '~/utils/cpcCoin';
import { formatMistAsDgc, formatMistAsData, mistToBigInt, shortenAddress } from '~/utils/explorerFormat';
import { displayValidatorHeading } from '~/utils/displayValidator';
import { sortValidatorsByStake } from '~/utils/sortValidatorsByStake';

type Props = {
	limit?: number;
};

const REFETCH_MS = 60_000;

function RankCell({ rank }: { rank: number }) {
	return (
		<Text variant="bodySmall/medium" color="steel">
			{rank}
		</Text>
	);
}

function CpcAmountCell({ mist }: { mist: string | undefined }) {
	const raw = String(mist ?? '0').trim();
	if (!raw || raw === '0') {
		return (
			<Text variant="bodySmall/medium" color="steel">
				—
			</Text>
		);
	}
	const m = mistToBigInt(raw);
	return (
		<div className="inline-flex items-baseline gap-1">
			<Text variant="bodySmall/medium">{formatMistAsDgc(m, { compact: true })}</Text>
			<span className="text-xs text-[#aeb9d0]">{CPC_SYMBOL}</span>
		</div>
	);
}

function DataAmountCell({ mist }: { mist: string | undefined }) {
	const raw = String(mist ?? '0').trim();
	if (!raw || raw === '0') {
		return (
			<Text variant="bodySmall/medium" color="steel">
				—
			</Text>
		);
	}
	const m = mistToBigInt(raw);
	return (
		<div className="inline-flex items-baseline gap-1">
			<Text variant="bodySmall/medium">{formatMistAsData(m, { compact: true })}</Text>
			<span className="text-xs text-[#aeb9d0]">DATA</span>
		</div>
	);
}

function ScopeBadge({ scope }: { scope?: string }) {
	if (scope === 'full_chain') {
		return (
			<span className="rounded border border-[#0ECB81]/30 bg-[#0ECB81]/10 px-1.5 py-px text-[10px] font-semibold uppercase text-[#0ECB81]">
				全链
			</span>
		);
	}

	return null;
}

function SummaryChip({
	label,
	value,
	hint,
}: {
	label: string;
	value: string;
	hint?: string;
}) {
	return (
		<div className="rounded-lg border border-[#18233E] bg-[#081223]/80 px-3 py-2">
			<p className="text-[10px] font-bold uppercase tracking-wider text-[#848E9C]">{label}</p>
			<p className="mt-0.5 text-lg font-semibold tabular-nums text-white">{value}</p>
			{hint ? <p className="mt-0.5 truncate text-[10px] text-[#5E6673]" title={hint}>{hint}</p> : null}
		</div>
	);
}

type PanelProps = {
	title: string;
	subtitle?: string;
	scope?: string;
	loading?: boolean;
	empty?: boolean;
	table: { data: object[]; columns: { header: string; accessorKey: string }[] } | null;
	limit: number;
	footer?: ReactNode;
};

function LeaderboardPanel({
	title,
	subtitle,
	scope,
	loading,
	empty,
	table,
	limit,
	footer,
}: PanelProps) {
	return (
		<div className="flex h-full flex-col rounded-xl border border-[#18233E] bg-gradient-to-b from-[#081223] to-[#0C1730] p-4 shadow-[0_0_40px_rgba(0,153,255,0.08)]">
			<div className="mb-3 flex flex-wrap items-start justify-between gap-2">
				<div className="min-w-0">
					<div className="flex flex-wrap items-center gap-2">
						<h3 className="text-sm font-bold text-white">{title}</h3>
						<ScopeBadge scope={scope} />
					</div>
					{subtitle ? (
						<p className="mt-1 text-[11px] leading-relaxed text-[#848E9C]">{subtitle}</p>
					) : null}
				</div>
			</div>

			{loading ? (
				<PlaceholderTable
					rowCount={Math.min(limit, 6)}
					rowHeight="13px"
					colHeadings={['#', 'Address', 'Value']}
					colWidths={['32px', '1fr', '80px']}
				/>
			) : null}

			{!loading && table ? (
				<div className="min-h-0 flex-1 overflow-x-auto">
					<TableCard data={table.data} columns={table.columns} />
				</div>
			) : null}

			{!loading && empty ? (
				<p className="py-6 text-center text-sm text-[#848E9C]">暂无数据</p>
			) : null}

			{footer ? <div className="mt-3 border-t border-white/10 pt-2">{footer}</div> : null}
		</div>
	);
}

export function CpcLeaderboardsCard({ limit = 8 }: Props) {
	const validatorsQuery = useSuiClientQuery('getLatestSuiSystemState');
	const healthQuery = useQuery({
		queryKey: ['cpc-leaderboard', 'health'],
		queryFn: fetchPushHubHealth,
		refetchInterval: REFETCH_MS,
		staleTime: 30_000,
	});
	const tradersQuery = useQuery({
		queryKey: ['cpc-leaderboard', 'traders', '24h', limit],
		queryFn: () => fetchClobTraderLeaderboard('24h', limit),
		refetchInterval: REFETCH_MS,
		staleTime: 30_000,
	});
	const ordersQuery = useQuery({
		queryKey: ['cpc-leaderboard', 'orders', limit],
		queryFn: () => fetchClobOrderLeaderboard(limit),
		refetchInterval: REFETCH_MS,
		staleTime: 30_000,
	});
	const cpcHoldersQuery = useQuery({
		queryKey: ['cpc-leaderboard', 'holders', limit],
		queryFn: () => fetchCpcHolderLeaderboard(limit),
		refetchInterval: REFETCH_MS,
		staleTime: 120_000,
	});
	const dataHoldersQuery = useQuery({
		queryKey: ['cpc-leaderboard', 'data-holders', limit],
		queryFn: () => fetchDataHolderLeaderboard(limit),
		refetchInterval: REFETCH_MS,
		staleTime: 120_000,
	});
	const userStakeQuery = useQuery({
		queryKey: ['cpc-leaderboard', 'stakers', limit],
		queryFn: () => fetchCpcStakerLeaderboard(limit),
		refetchInterval: REFETCH_MS,
		staleTime: 120_000,
	});

	const validatorTable = useMemo(() => {
		const validators = validatorsQuery.data?.activeValidators;
		if (!validators?.length) return null;
		const sorted = sortValidatorsByStake(validators).slice(0, limit);
		return {
			data: sorted.map((v, index) => {
				const heading = displayValidatorHeading(v.name, v.description);
				return {
				rank: <RankCell rank={index + 1} />,
				name: (
					<div className="flex min-w-0 items-center gap-2 overflow-hidden">
						<ImageIcon src={v.imageUrl} size="sm" fallback={heading} label={heading} circle />
						<ValidatorLink address={v.suiAddress} label={heading} />
					</div>
				),
				stake: (
					<LeaderboardValueCell>
						<CpcAmountCell mist={v.stakingPoolSuiBalance} />
					</LeaderboardValueCell>
				),
			};
			}),
			columns: [
				{ header: '#', accessorKey: 'rank', meta: RANK_COLUMN_META },
				{ header: 'Validator', accessorKey: 'name', meta: ADDRESS_COLUMN_META },
				{ header: 'Pool Stake', accessorKey: 'stake', meta: VALUE_COLUMN_META },
			],
		};
	}, [validatorsQuery.data, limit]);

	const traderTable = useMemo(() => {
		const rows = tradersQuery.data;
		if (!rows?.length) return null;
		return buildTraderTable(rows, limit);
	}, [tradersQuery.data, limit]);

	const orderTable = useMemo(() => {
		const rows = ordersQuery.data;
		if (!rows?.length) return null;
		return buildOrderTable(rows, limit);
	}, [ordersQuery.data, limit]);

	const cpcHolderTable = useMemo(() => {
		const rows = cpcHoldersQuery.data?.rows;
		if (!rows?.length) return null;
		return buildCpcHolderTable(rows, limit);
	}, [cpcHoldersQuery.data, limit]);

	const dataHolderTable = useMemo(() => {
		const rows = dataHoldersQuery.data?.rows;
		if (!rows?.length) return null;
		return buildDataHolderTable(rows, limit);
	}, [dataHoldersQuery.data, limit]);

	const userStakeTable = useMemo(() => {
		const rows = userStakeQuery.data?.rows;
		if (!rows?.length) return null;
		return buildStakeTable(rows, limit);
	}, [userStakeQuery.data, limit]);

	const tradeStats = healthQuery.data?.trade_stats;
	const validatorCount = validatorsQuery.data?.activeValidators.length ?? 0;

	return (
		<div className="flex w-full flex-col gap-4">
			<div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6">
				<SummaryChip label="验证者" value={String(validatorCount)} hint="活跃验证者池" />
				<SummaryChip
					label={`${CPC_SYMBOL} 持币地址`}
					value={String(cpcHoldersQuery.data?.sample_size ?? cpcHoldersQuery.data?.rows?.length ?? '—')}
					hint={cpcHoldersQuery.data?.scope === 'full_chain' ? 'Indexer 全链扫描' : 'CLOB 地址采样'}
				/>
				<SummaryChip
					label="DATA 持币地址"
					value={String(dataHoldersQuery.data?.sample_size ?? dataHoldersQuery.data?.rows?.length ?? '—')}
					hint={dataHoldersQuery.data?.scope === 'full_chain' ? 'Indexer 全链扫描' : 'CLOB 地址采样'}
				/>
				<SummaryChip
					label="24h 成交笔数"
					value={formatAmount(tradeStats?.recent_24h_count ?? 0)}
					hint={`${formatAmount(tradeStats?.unique_traders_24h ?? 0)} 活跃地址`}
				/>
				<SummaryChip
					label="24h 成交量"
					value={`${formatAmount(Number(tradeStats?.recent_24h_volume_lots ?? 0))} lots`}
					hint={`CLOB DATA/${CPC_SYMBOL}`}
				/>
				<SummaryChip
					label="挂单"
					value={formatAmount(healthQuery.data?.order_stats?.open_orders ?? 0)}
					hint={`累计 ${formatAmount(healthQuery.data?.order_stats?.total_orders ?? 0)} 笔`}
				/>
			</div>

			<div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
				<LeaderboardPanel
					title="验证者质押"
					subtitle="链上验证者池质押（RPC）"
					loading={validatorsQuery.isPending}
					empty={!validatorsQuery.isPending && !validatorTable}
					table={validatorTable}
					limit={limit}
					footer={
						<Link to={{ pathname: '/stake', hash: '#validators' }}>
							<div className="flex items-center gap-2 text-xs text-[#aeb9d0]">
								查看全部验证者
								<ArrowRight12 fill="currentColor" className="h-3 w-3 -rotate-45" />
							</div>
						</Link>
					}
				/>
				<LeaderboardPanel
					title={`${CPC_SYMBOL} 持币排行`}
					subtitle={cpcHoldersQuery.data?.disclaimer}
					scope={cpcHoldersQuery.data?.scope}
					loading={cpcHoldersQuery.isPending}
					empty={!cpcHoldersQuery.isPending && !cpcHolderTable}
					table={cpcHolderTable}
					limit={limit}
				/>
				<LeaderboardPanel
					title="DATA 持币排行"
					subtitle={dataHoldersQuery.data?.disclaimer}
					scope={dataHoldersQuery.data?.scope}
					loading={dataHoldersQuery.isPending}
					empty={!dataHoldersQuery.isPending && !dataHolderTable}
					table={dataHolderTable}
					limit={limit}
					footer={
						<Link to="/trade">
							<div className="flex items-center gap-2 text-xs text-[#aeb9d0]">
								前往 DEX 交易
								<ArrowRight12 fill="currentColor" className="h-3 w-3 -rotate-45" />
							</div>
						</Link>
					}
				/>
				<LeaderboardPanel
					title="用户委托质押"
					subtitle={userStakeQuery.data?.disclaimer}
					scope={userStakeQuery.data?.scope}
					loading={userStakeQuery.isPending}
					empty={!userStakeQuery.isPending && !userStakeTable}
					table={userStakeTable}
					limit={limit}
					footer={
						<Link to="/stake">
							<div className="flex items-center gap-2 text-xs text-[#aeb9d0]">
								质押页面
								<ArrowRight12 fill="currentColor" className="h-3 w-3 -rotate-45" />
							</div>
						</Link>
					}
				/>
				<LeaderboardPanel
					title="CLOB 成交量 (24h)"
					subtitle="Push Hub 统计的链下成交活跃度"
					loading={tradersQuery.isPending}
					empty={!tradersQuery.isPending && !traderTable}
					table={traderTable}
					limit={limit}
				/>
				<LeaderboardPanel
					title="CLOB 挂单排行"
					subtitle="按历史挂单数量排序"
					loading={ordersQuery.isPending}
					empty={!ordersQuery.isPending && !orderTable}
					table={orderTable}
					limit={limit}
				/>
			</div>
		</div>
	);
}

const RANK_COLUMN_META = {
	align: 'center' as const,
	headerClassName: 'w-9',
	cellClassName: 'w-9',
};

const ADDRESS_COLUMN_META = {
	align: 'left' as const,
	headerClassName: 'w-auto',
	cellClassName: 'max-w-0 overflow-hidden',
};

const VALUE_COLUMN_META = {
	align: 'right' as const,
	headerClassName: 'w-[7.5rem] whitespace-nowrap',
	cellClassName: 'w-[7.5rem] whitespace-nowrap tabular-nums',
};

const VOLUME_COLUMN_META = {
	align: 'right' as const,
	headerClassName: 'w-[11rem] whitespace-nowrap',
	cellClassName: 'w-[11rem] whitespace-nowrap tabular-nums',
};

/** 排行榜地址：比默认多显示 4 位（10+8 vs 8+6） */
const LEADERBOARD_ADDRESS_HEAD = 10;
const LEADERBOARD_ADDRESS_TAIL = 8;

const LEADERBOARD_ADDRESS_LINK_CLASS =
	'block w-full truncate !whitespace-nowrap !break-normal font-mono text-sm lowercase text-[#38BDF8] hover:text-[#7DD3FC] hover:underline';

function LeaderboardAddressCell({ address }: { address: string }) {
	return (
		<AddressLink
			address={address}
			label={shortenAddress(address, LEADERBOARD_ADDRESS_HEAD, LEADERBOARD_ADDRESS_TAIL)}
			className={LEADERBOARD_ADDRESS_LINK_CLASS}
		/>
	);
}

function LeaderboardValueCell({ children }: { children: React.ReactNode }) {
	return <div className="flex justify-end">{children}</div>;
}

function buildTraderTable(rows: ClobTraderRank[], limit: number) {
	return {
		data: rows.slice(0, limit).map((row, index) => ({
			rank: <RankCell rank={index + 1} />,
			address: <LeaderboardAddressCell address={row.address} />,
			volume: (
				<LeaderboardValueCell>
					<span className="whitespace-nowrap text-sm">
						<span className="font-medium text-[#aeb9d0]">
							{formatAmount(row.volume_lots ?? 0)} lots
						</span>
						<span className="mx-1.5 text-[#64748b]">·</span>
						<span className="text-[#94a3b8]">{row.trade_count ?? 0} 笔</span>
					</span>
				</LeaderboardValueCell>
			),
		})),
		columns: [
			{ header: '#', accessorKey: 'rank', meta: RANK_COLUMN_META },
			{ header: 'Address', accessorKey: 'address', meta: ADDRESS_COLUMN_META },
			{
				header: 'Volume (24h)',
				accessorKey: 'volume',
				meta: VOLUME_COLUMN_META,
			},
		],
	};
}

function buildOrderTable(rows: ClobOrderRank[], limit: number) {
	return {
		data: rows.slice(0, limit).map((row, index) => ({
			rank: <RankCell rank={index + 1} />,
			address: <LeaderboardAddressCell address={row.address} />,
			orders: (
				<LeaderboardValueCell>
					<Text variant="bodySmall/medium">{formatAmount(row.order_count ?? 0)}</Text>
				</LeaderboardValueCell>
			),
		})),
		columns: [
			{ header: '#', accessorKey: 'rank', meta: RANK_COLUMN_META },
			{ header: 'Address', accessorKey: 'address', meta: ADDRESS_COLUMN_META },
			{ header: 'Orders', accessorKey: 'orders', meta: VALUE_COLUMN_META },
		],
	};
}

function buildCpcHolderTable(rows: AddressStatRank[], limit: number) {
	return {
		data: rows.slice(0, limit).map((row, index) => ({
			rank: <RankCell rank={index + 1} />,
			address: <LeaderboardAddressCell address={row.address} />,
			balance: (
				<LeaderboardValueCell>
					<CpcAmountCell mist={row.cpc_mist} />
				</LeaderboardValueCell>
			),
		})),
		columns: [
			{ header: '#', accessorKey: 'rank', meta: RANK_COLUMN_META },
			{ header: 'Address', accessorKey: 'address', meta: ADDRESS_COLUMN_META },
			{ header: `${CPC_SYMBOL} Balance`, accessorKey: 'balance', meta: VALUE_COLUMN_META },
		],
	};
}

function buildDataHolderTable(rows: AddressStatRank[], limit: number) {
	return {
		data: rows.slice(0, limit).map((row, index) => ({
			rank: <RankCell rank={index + 1} />,
			address: <LeaderboardAddressCell address={row.address} />,
			balance: (
				<LeaderboardValueCell>
					<DataAmountCell mist={row.data_mist} />
				</LeaderboardValueCell>
			),
		})),
		columns: [
			{ header: '#', accessorKey: 'rank', meta: RANK_COLUMN_META },
			{ header: 'Address', accessorKey: 'address', meta: ADDRESS_COLUMN_META },
			{ header: 'DATA Balance', accessorKey: 'balance', meta: VALUE_COLUMN_META },
		],
	};
}

function buildStakeTable(rows: AddressStatRank[], limit: number) {
	return {
		data: rows.slice(0, limit).map((row, index) => ({
			rank: <RankCell rank={index + 1} />,
			address: <LeaderboardAddressCell address={row.address} />,
			stake: (
				<LeaderboardValueCell>
					<CpcAmountCell mist={row.stake_mist} />
				</LeaderboardValueCell>
			),
		})),
		columns: [
			{ header: '#', accessorKey: 'rank', meta: RANK_COLUMN_META },
			{ header: 'Address', accessorKey: 'address', meta: ADDRESS_COLUMN_META },
			{ header: 'Delegated Stake', accessorKey: 'stake', meta: VALUE_COLUMN_META },
		],
	};
}
